/**
 * Desktop Dictation Hotkey Hook
 *
 * Handles global Fn/Globe key detection for desktop dictation.
 * The native widget provides all dictation UI feedback on desktop;
 * this hook handles cancellation guards and config sync only.
 */

import { onceSafeUnlisten, type UnlistenFn } from '@heidi/plugin-core';
import { isTauri } from '@heidi/runtime/platform';
import { GET_USER } from '@heidi/workspace/contracts';
import { GET_USER_SNIPPETS } from '@heidi/workspace/data';
import { QueryObserver, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import { DICTATION_CUSTOM_VOCABULARY_KEY } from '../../../../contracts/custom-vocabulary-query-keys';
import {
  type DictationBridgeProcessingPayload as DictationProcessingPayload,
  type DictationBridgeResultPayload as DictationResultPayload,
  type DictationBridgeStartPayload as DictationStartPayload,
} from '../../../../contracts/dictation-bridge.types';
import { DICTATION_APP_CATEGORIES_KEY } from '../../../../contracts/preferences-query-keys';
import { getDictationAppCategories } from '../../../../data/api/preferences';
import { getDictationBridgePort } from '../../../../dictation-bridge-ports';
import {
  useIsCursorAwarenessEnabled,
  useIsDictationCustomVocabularyEnabled,
} from '../../../../flags';
import { onboardingDictationActiveAtom } from '../../../onboarding-flow/atoms';
import { getDictationBubblePorts } from '../../bubble-ports';
import { getDictationBubbleUiHost } from '../../host';
import { dictationLogger } from '../../store/dictationLogger';
import { useDictationStore } from '../../store/useDictationStore';
import { canStartDictation, isSessionRecordingActive } from './dictationHotkeyHelpers';
import { useInvalidateDictationContextOnSessionCreate } from './useInvalidateDictationContextOnSessionCreate';

/** Hook that handles desktop global hotkey events for dictation. */
export const useDesktopDictationHotkey = (): void => {
  const queryClient = useQueryClient();
  const isDesktopDictationEnabled = getDictationBubblePorts().useIsDictationEnabled();
  // Post-sign-in onboarding steps need the guard/config listeners even before
  // the user's `dictation.enabled` profile flag is set (SCR-4853).
  const isOnboardingDictationActive = useAtomValue(onboardingDictationActiveAtom);
  const enabled = isDesktopDictationEnabled || isOnboardingDictationActive;

  // Kill-switch flag resolves async and can flip at runtime; no GET_USER/focus observer
  // below fires on a flag-only change, so resync Rust when the resolved value changes.
  const isCursorAwarenessEnabled = useIsCursorAwarenessEnabled();
  const prevCursorAwarenessRef = useRef(isCursorAwarenessEnabled);
  const isCustomVocabularyEnabled = useIsDictationCustomVocabularyEnabled();
  const prevCustomVocabularyRef = useRef(isCustomVocabularyEnabled);

  // Desktop side; web mounts the same hook from DictationBubble.
  useInvalidateDictationContextOnSessionCreate(isTauri() && enabled);

  // Ruling 25's second composition point: this hook reuses the ONE widget
  // port type (`DictationWidgetPort`) through the landed binding rather than
  // declaring its own `hide`-shaped port.
  const bridge = getDictationBridgePort();

  useEffect(() => {
    // Only run on desktop with feature flag enabled (or onboarding dictation active)
    if (!isTauri() || !enabled) return;

    let active = true;
    const unlisteners: UnlistenFn[] = [];

    // SCR-4525: shared post-Start work — flag `nativeDictationActive` (so the
    // in-app `DictateButton` blocks further clicks while a session is in
    // flight, regardless of trigger), and for button-triggered sessions flip
    // the main-window status to LISTENING so the same button renders its
    // Loader/Stop state. Analytics dispatch is owned by `useWidgetDictationEvents`
    // (post-DEV-11909) — both hotkey and button paths fire from there using
    // payload.triggeredBy.
    const applyStartSideEffects = (payload: DictationStartPayload) => {
      if (!payload.recordingStarted) return;
      useDictationStore.getState().setNativeDictationActive(true);
      if (payload.triggeredBy === 'button_click_evidence') {
        // Defensive: handleClick set these already, but ensure source/mode are
        // correct so the button's source-gated state selectors fire properly.
        useDictationStore.setState({
          status: 'LISTENING',
          startedAt: new Date(),
          source: 'webDictateButton',
          dictationMode: 'HANDSFREE',
        });
        return;
      }
      // Hotkey-triggered session: explicitly clear `source` so a stale
      // 'webDictateButton' left by a prior failed-Rust button click can't
      // misroute this session through the button-only processing/result
      // paths below (`handleDictationProcessing` / `handleDictationResult`
      // gate on `source === 'webDictateButton'`).
      if (useDictationStore.getState().source === 'webDictateButton') {
        useDictationStore.setState({ source: null, dictationMode: null });
      }
    };

    // Handle dictation:start: guard only — cancel if dictation shouldn't proceed.
    // The native widget handles all UI feedback; this hook just enforces guards.
    const handleDictationStart = async (payload: DictationStartPayload) => {
      // SCR-4118: write Rust-minted IDs into the store before logging so the
      // log enrichment in `dictationLogger` picks them up. Only present when
      // Rust actually started recording.
      if (payload.dictationId && payload.dictationRecordingId) {
        useDictationStore.getState().setDictationIds({
          dictationId: payload.dictationId,
          dictationRecordingId: payload.dictationRecordingId,
        });
      }
      dictationLogger.info({
        event_type: 'DESKTOP_DICTATION_START_RECEIVED',
        details: {
          recording_started: payload.recordingStarted,
          triggered_by: payload.triggeredBy,
          ...(payload.error ? { error_reason: payload.error } : {}),
        },
      });

      if (!canStartDictation() || isSessionRecordingActive()) {
        if (payload.recordingStarted) {
          // Cancel + dismiss together. Without the widgetHide, the widget would stay
          // on LISTENING with no recording behind it, leaving the user stuck.
          await bridge.cancelNativeDictation();
          await getDictationBubbleUiHost().hideDictationWidget();
        }
        return;
      }

      if (!payload.recordingStarted) {
        dictationLogger.error({
          event_type: 'DESKTOP_DICTATION_NATIVE_RECORDING_FAILED',
          details: { error_reason: payload.error },
        });
        return;
      }

      applyStartSideEffects(payload);
    };

    // Handle handsfree-start: same guard logic as dictation:start.
    const handleHandsfreeStart = async (payload: DictationStartPayload) => {
      if (payload.dictationId && payload.dictationRecordingId) {
        useDictationStore.getState().setDictationIds({
          dictationId: payload.dictationId,
          dictationRecordingId: payload.dictationRecordingId,
        });
      }
      dictationLogger.info({
        event_type: 'DESKTOP_DICTATION_HANDSFREE_START_RECEIVED',
        details: {
          recording_started: payload.recordingStarted,
          triggered_by: payload.triggeredBy,
          ...(payload.error ? { error_reason: payload.error } : {}),
        },
      });

      if (!canStartDictation() || isSessionRecordingActive()) {
        if (payload.recordingStarted) {
          await bridge.cancelNativeDictation();
          await getDictationBubbleUiHost().hideDictationWidget();
        }
        return;
      }

      if (!payload.recordingStarted) {
        dictationLogger.error({
          event_type: 'DESKTOP_DICTATION_HANDSFREE_RECORDING_FAILED',
          details: { error_reason: payload.error },
        });
        return;
      }

      applyStartSideEffects(payload);
    };

    // SCR-4525: when the active session is button-triggered, mirror Rust's
    // PROCESSING state into the main-window store so the in-app button
    // renders its Loader. Hotkey-triggered sessions don't touch main-window
    // status (the widget owns their UI), so we gate on the source we wrote
    // in `applyStartSideEffects`.
    const handleDictationProcessing = () => {
      const state = useDictationStore.getState();
      if (state.source === 'webDictateButton' && state.status === 'LISTENING') {
        useDictationStore.setState({ status: 'PROCESSING' });
      }
    };

    // Helper to subscribe to Tauri events
    const subscribe = <T>(
      listener: (
        cb: (event: { payload: T }) => void | Promise<void>
      ) => Promise<() => void>,
      handler: (payload: T) => void | Promise<void>,
      name: string
    ) => {
      listener((event) => {
        void Promise.resolve(handler(event.payload)).catch((err: unknown) => {
          dictationLogger.error({
            event_type: 'DESKTOP_DICTATION_EVENT_HANDLER_FAILED',
            details: {
              listener: name,
              errorName: err instanceof Error ? err.name : typeof err,
            },
          });
        });
      })
        .then((rawUnlisten) => {
          // Tauri's unlisten returns a promise that rejects when its listener
          // entry is already gone. Wrap it so teardown cannot raise an
          // unhandled rejection and runs at most once.
          const unlisten = onceSafeUnlisten(rawUnlisten, (err: unknown) => {
            dictationLogger.info({
              event_type: 'DESKTOP_DICTATION_LISTENER_ALREADY_REMOVED',
              details: {
                listener: name,
                errorName: err instanceof Error ? err.name : typeof err,
              },
            });
          });
          if (active) unlisteners.push(unlisten);
          else unlisten();
        })
        .catch((err: unknown) => {
          dictationLogger.error({
            event_type: 'DESKTOP_DICTATION_LISTENER_SETUP_FAILED',
            details: {
              listener: name,
              errorName: err instanceof Error ? err.name : typeof err,
            },
          });
        });
    };

    // Handle dictation:result — clear the store's correlation IDs so any
    // log fired between session end and the next start is not misattributed
    // to the dead session. The widget owns user-facing result UI and analytics
    // (DEV-11909); this hook just resets the main-window button-UI fields.
    //
    // SCR-4525: also clear `nativeDictationActive`, and for button-triggered
    // sessions reset status / source / mode so the in-app button returns to Mic.
    const handleDictationResult = () => {
      const state = useDictationStore.getState();
      const wasButtonTriggered = state.source === 'webDictateButton';
      state.clearDictationIds();
      state.setNativeDictationActive(false);
      if (wasButtonTriggered) {
        useDictationStore.setState({
          status: 'INACTIVE',
          source: null,
          dictationMode: null,
          startedAt: null,
        });
      }
    };

    // Subscribe to dictation events (guard-only — widget handles UI)
    subscribe<DictationStartPayload>(
      (cb) =>
        bridge.listenDictationStart((event) => {
          void cb(event);
        }),
      handleDictationStart,
      'start'
    );
    subscribe<DictationStartPayload>(
      (cb) =>
        bridge.listenHandsfreeStart((event) => {
          void cb(event);
        }),
      handleHandsfreeStart,
      'handsfree-start'
    );
    subscribe<DictationProcessingPayload>(
      (cb) =>
        bridge.listenDictationProcessing((event) => {
          void cb(event);
        }),
      handleDictationProcessing,
      'processing'
    );
    subscribe<DictationResultPayload>(
      (cb) =>
        bridge.listenDictationResult((event) => {
          void cb(event);
        }),
      handleDictationResult,
      'result'
    );

    // Initialize config on mount
    bridge.updateNativeDictationConfig().catch((err: unknown) => {
      dictationLogger.error({
        event_type: 'DESKTOP_DICTATION_CONFIG_UPDATE_FAILED',
        details: {
          context: 'mount',
          errorName: err instanceof Error ? err.name : typeof err,
        },
      });
    });

    // Re-sync config when user data changes (e.g. default languages updated)
    const getUserObserver = new QueryObserver(queryClient, {
      queryKey: [GET_USER],
      enabled: false,
    });
    let lastDataUpdatedAt = getUserObserver.getCurrentResult().dataUpdatedAt;
    const unsubscribeLanguages = getUserObserver.subscribe((result) => {
      if (result.dataUpdatedAt !== lastDataUpdatedAt) {
        lastDataUpdatedAt = result.dataUpdatedAt;
        bridge.updateNativeDictationConfig().catch((err: unknown) => {
          dictationLogger.error({
            event_type: 'DESKTOP_DICTATION_CONFIG_UPDATE_FAILED',
            details: {
              context: 'language_change',
              errorName: err instanceof Error ? err.name : typeof err,
            },
          });
        });
      }
    });

    // Re-sync config when snippets change so Rust forwards fresh snippets.
    const snippetsObserver = new QueryObserver(queryClient, {
      queryKey: [GET_USER_SNIPPETS],
      enabled: false,
    });
    let lastSnippetsUpdatedAt = snippetsObserver.getCurrentResult().dataUpdatedAt;
    const unsubscribeSnippets = snippetsObserver.subscribe((result) => {
      if (result.dataUpdatedAt !== lastSnippetsUpdatedAt) {
        lastSnippetsUpdatedAt = result.dataUpdatedAt;
        bridge.updateNativeDictationConfig().catch((err: unknown) => {
          dictationLogger.error({
            event_type: 'DESKTOP_DICTATION_CONFIG_UPDATE_FAILED',
            details: {
              context: 'snippets_change',
              errorName: err instanceof Error ? err.name : typeof err,
            },
          });
        });
      }
    });

    // Re-sync config when the custom vocabulary changes (tab CRUD mutates it)
    // so Rust forwards fresh terms. `enabled: false` — the fetch is owned by
    // `ensureCurrentCustomVocabulary` in the config builder and the tab's hook.
    const customVocabularyObserver = new QueryObserver(queryClient, {
      queryKey: [DICTATION_CUSTOM_VOCABULARY_KEY],
      enabled: false,
    });
    let lastCustomVocabularyUpdatedAt =
      customVocabularyObserver.getCurrentResult().dataUpdatedAt;
    const unsubscribeCustomVocabulary = customVocabularyObserver.subscribe((result) => {
      if (result.dataUpdatedAt !== lastCustomVocabularyUpdatedAt) {
        lastCustomVocabularyUpdatedAt = result.dataUpdatedAt;
        bridge.updateNativeDictationConfig().catch((err: unknown) => {
          dictationLogger.error({
            event_type: 'DESKTOP_DICTATION_CONFIG_UPDATE_FAILED',
            details: {
              context: 'custom_vocabulary_change',
              errorName: err instanceof Error ? err.name : typeof err,
            },
          });
        });
      }
    });

    // Warm the cache + re-sync on tone-map changes.
    const appCategoriesObserver = new QueryObserver(queryClient, {
      queryKey: [DICTATION_APP_CATEGORIES_KEY],
      queryFn: getDictationAppCategories,
    });
    let lastCategoriesUpdatedAt = appCategoriesObserver.getCurrentResult().dataUpdatedAt;
    const unsubscribeCategories = appCategoriesObserver.subscribe((result) => {
      if (result.dataUpdatedAt !== lastCategoriesUpdatedAt) {
        lastCategoriesUpdatedAt = result.dataUpdatedAt;
        bridge.updateNativeDictationConfig().catch((err: unknown) => {
          dictationLogger.error({
            event_type: 'DESKTOP_DICTATION_CONFIG_UPDATE_FAILED',
            details: {
              context: 'tone_change',
              errorName: err instanceof Error ? err.name : typeof err,
            },
          });
        });
      }
    });

    // Re-sync config on window focus (user may have changed settings)
    const handleFocus = () => {
      bridge.updateNativeDictationConfig().catch((err: unknown) => {
        dictationLogger.error({
          event_type: 'DESKTOP_DICTATION_CONFIG_UPDATE_FAILED',
          details: {
            context: 'window_focus',
            errorName: err instanceof Error ? err.name : typeof err,
          },
        });
      });
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      active = false;
      unlisteners.forEach((unlisten) => {
        unlisten();
      });
      unsubscribeLanguages();
      getUserObserver.destroy();
      unsubscribeSnippets();
      snippetsObserver.destroy();
      unsubscribeCustomVocabulary();
      customVocabularyObserver.destroy();
      unsubscribeCategories();
      appCategoriesObserver.destroy();
      window.removeEventListener('focus', handleFocus);
    };
  }, [enabled, queryClient, bridge]);

  useEffect(() => {
    if (!isTauri() || !enabled) return;
    if (prevCursorAwarenessRef.current === isCursorAwarenessEnabled) return;
    prevCursorAwarenessRef.current = isCursorAwarenessEnabled;
    bridge.updateNativeDictationConfig().catch((err: unknown) => {
      dictationLogger.error({
        event_type: 'DESKTOP_DICTATION_CONFIG_UPDATE_FAILED',
        details: {
          context: 'cursor_awareness_flag_change',
          errorName: err instanceof Error ? err.name : typeof err,
        },
      });
    });
  }, [bridge, enabled, isCursorAwarenessEnabled]);

  useEffect(() => {
    if (!isTauri() || !enabled) return;
    if (prevCustomVocabularyRef.current === isCustomVocabularyEnabled) return;
    prevCustomVocabularyRef.current = isCustomVocabularyEnabled;
    bridge.updateNativeDictationConfig().catch((err: unknown) => {
      dictationLogger.error({
        event_type: 'DESKTOP_DICTATION_CONFIG_UPDATE_FAILED',
        details: {
          context: 'custom_vocabulary_flag_change',
          errorName: err instanceof Error ? err.name : typeof err,
        },
      });
    });
  }, [bridge, enabled, isCustomVocabularyEnabled]);
};
